import { createContext, useContext } from 'react'
import type { PortalRole } from '../lib/types'

export type AuthStatus = 'checking' | 'authenticated' | 'unauthenticated'

export interface AuthProfile {
  displayName?: string | null
  email?: string | null
  accountId?: string | null
  accountName?: string | null
  role?: PortalRole | null
}

/** Why a session ended: `expired` shows "session expired" on Login,
 * `logout`/`provider_logout` the "signed out" badge, and `denied` (a revoked
 * grant, D10) neither — Access denied has its own screen. */
export type LogoutReason = 'expired' | 'logout' | 'provider_logout' | 'denied'

export interface AuthContextValue {
  isAuthenticated: boolean
  status: AuthStatus
  // Two methods rather than one boolean-flag setter (Sonar typescript:S2301)
  // — login/logout are different enough (a profile to store vs. a reason to
  // record) that one signature covering both was really two APIs wearing a
  // shared name.
  login: (profile?: AuthProfile) => void
  logout: (reason?: LogoutReason) => void
  /** Re-reads GET /auth/session into the profile (MS-997 D1): after a 403
   * `auth.portal_admin_required`, or a change to the signed-in Admin's own
   * row, so the role — and with it the Settings nav item and route guard —
   * follows the grant. */
  refreshSession: () => Promise<void>
  userEmail: string | null
  userName: string | null
  accountId: string | null
  accountName: string | null
  /** The grant's role, from the session (MS-997). */
  role: PortalRole | null
  /** `role === 'admin'`: Settings › Access is shown. */
  isPortalAdmin: boolean
  /** False while the mount probe is still answering, or while the profile
   * carries no role (one cached before MS-997): a role guard waits rather
   * than sending an Admin Home on a stale or missing role. */
  roleKnown: boolean
  didLogOut: boolean
  didExpire: boolean
}

export const AuthContext = createContext<AuthContextValue | null>(null)

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider')
  return ctx
}
